import { Fragment, type ReactNode } from 'react'

type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3 | 4; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'code'; code: string; lang: string }
  | { type: 'image'; alt: string; url: string }
  | { type: 'video'; label: string; url: string }
  | { type: 'quote'; text: string }
  | { type: 'hr' }

interface MarkdownArticleProps {
  markdown: string
  className?: string
}

function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0]
  return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov') || lower.endsWith('.m4v')
}

function parseMarkdown(markdown: string): MarkdownBlock[] {
  const lines = markdown.split('\n')
  const blocks: MarkdownBlock[] = []
  let i = 0

  while (i < lines.length) {
    const rawLine = lines[i]
    const line = rawLine.trim()

    if (!line) {
      i += 1
      continue
    }

    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, '').trim()
      const codeLines: string[] = []
      i += 1
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i])
        i += 1
      }
      if (i < lines.length) {
        i += 1
      }
      blocks.push({ type: 'code', code: codeLines.join('\n'), lang })
      continue
    }

    if (/^---+$/.test(line)) {
      blocks.push({ type: 'hr' })
      i += 1
      continue
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length as 1 | 2 | 3 | 4
      blocks.push({ type: 'heading', level, text: headingMatch[2].trim() })
      i += 1
      continue
    }

    const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
    if (imageMatch) {
      blocks.push({
        type: 'image',
        alt: imageMatch[1].trim() || 'report image',
        url: imageMatch[2].trim(),
      })
      i += 1
      continue
    }

    const reportImageMatch = line.match(/^Image\s+\d+:\s+!\[([^\]]*)\]\(([^)]+)\)$/)
    if (reportImageMatch) {
      blocks.push({
        type: 'image',
        alt: reportImageMatch[1].trim() || 'report image',
        url: reportImageMatch[2].trim(),
      })
      i += 1
      continue
    }

    const reportVideoMatch = line.match(/^Video\s+\d+:\s+\[[^\]]*\]\((https?:\/\/[^)]+)\)$/)
    if (reportVideoMatch) {
      blocks.push({
        type: 'video',
        label: line.replace(/^Video\s+\d+:\s+/, ''),
        url: reportVideoMatch[1].trim(),
      })
      i += 1
      continue
    }

    if (/^>\s?/.test(line)) {
      const quoteLines: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''))
        i += 1
      }
      blocks.push({ type: 'quote', text: quoteLines.join(' ') })
      continue
    }

    if (/^-\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^-\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^-\s+/, '').trim())
        i += 1
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, '').trim())
        i += 1
      }
      blocks.push({ type: 'ol', items })
      continue
    }

    const paragraphLines: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,4})\s+/.test(lines[i].trim()) &&
      !/^-\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim()) &&
      !/^>\s?/.test(lines[i].trim()) &&
      !/^```/.test(lines[i].trim()) &&
      !/^---+$/.test(lines[i].trim()) &&
      !/^!\[([^\]]*)\]\(([^)]+)\)$/.test(lines[i].trim()) &&
      !/^Image\s+\d+:\s+!\[([^\]]*)\]\(([^)]+)\)$/.test(lines[i].trim()) &&
      !/^Video\s+\d+:\s+\[[^\]]*\]\((https?:\/\/[^)]+)\)$/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim())
      i += 1
    }
    blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') })
  }

  return blocks
}

function renderInline(text: string) {
  const pattern =
    /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^)]+)\)|https?:\/\/[^\s<>()]+)/g
  const result: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  match = pattern.exec(text)
  while (match) {
    if (match.index > lastIndex) {
      result.push(<Fragment key={`txt-${key++}`}>{text.slice(lastIndex, match.index)}</Fragment>)
    }

    const token = match[0]
    if (token.startsWith('**') && token.endsWith('**')) {
      result.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('`') && token.endsWith('`')) {
      result.push(
        <code key={`c-${key++}`} className="px-1 py-0.5 rounded bg-gray-100 text-gray-800 text-[0.9em]">
          {token.slice(1, -1)}
        </code>
      )
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/)
      if (linkMatch) {
        const [, label, url] = linkMatch
        result.push(
          <a
            key={`l-${key++}`}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-emerald-700 hover:text-emerald-800 underline break-all"
          >
            {label}
          </a>
        )
      } else {
        result.push(<Fragment key={`txt-${key++}`}>{token}</Fragment>)
      }
    } else {
      result.push(
        <a
          key={`u-${key++}`}
          href={token}
          target="_blank"
          rel="noreferrer"
          className="text-emerald-700 hover:text-emerald-800 underline break-all"
        >
          {token}
        </a>
      )
    }

    lastIndex = pattern.lastIndex
    match = pattern.exec(text)
  }

  if (lastIndex < text.length) {
    result.push(<Fragment key={`txt-${key++}`}>{text.slice(lastIndex)}</Fragment>)
  }

  return result
}

export function MarkdownArticle({ markdown, className }: MarkdownArticleProps) {
  const blocks = parseMarkdown(markdown)

  return (
    <article className={className}>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          if (block.level === 1) {
            return <h1 key={index} className="text-xl font-bold text-gray-900 mt-1 mb-3">{block.text}</h1>
          }
          if (block.level === 2) {
            return <h2 key={index} className="text-lg font-semibold text-gray-900 mt-5 mb-2">{block.text}</h2>
          }
          if (block.level === 3) {
            return <h3 key={index} className="text-base font-semibold text-gray-900 mt-4 mb-2">{block.text}</h3>
          }
          return <h4 key={index} className="text-sm font-semibold text-gray-900 mt-3 mb-1.5">{block.text}</h4>
        }

        if (block.type === 'paragraph') {
          return (
            <p key={index} className="text-sm leading-6 text-gray-700 mb-2.5">
              {renderInline(block.text)}
            </p>
          )
        }

        if (block.type === 'ul') {
          return (
            <ul key={index} className="list-disc pl-5 mb-3 space-y-1.5 text-sm text-gray-700">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          )
        }

        if (block.type === 'ol') {
          return (
            <ol key={index} className="list-decimal pl-5 mb-3 space-y-1.5 text-sm text-gray-700">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ol>
          )
        }

        if (block.type === 'quote') {
          return (
            <blockquote
              key={index}
              className="border-l-4 border-emerald-300 bg-emerald-50/60 px-3 py-2 rounded-r-md text-sm text-gray-700 mb-3"
            >
              {renderInline(block.text)}
            </blockquote>
          )
        }

        if (block.type === 'code') {
          return (
            <pre key={index} className="text-xs leading-relaxed bg-gray-950 text-gray-100 rounded-md p-3 mb-3 overflow-auto">
              <code>{block.code}</code>
            </pre>
          )
        }

        if (block.type === 'image') {
          return (
            <figure key={index} className="mb-3">
              <img
                src={block.url}
                alt={block.alt}
                loading="lazy"
                className="w-full max-h-80 object-cover rounded-md border border-gray-200"
              />
              <figcaption className="text-xs text-gray-500 mt-1">{block.alt}</figcaption>
            </figure>
          )
        }

        if (block.type === 'video') {
          return (
            <div key={index} className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-2.5">
              {isVideoUrl(block.url) ? (
                <video
                  controls
                  preload="none"
                  className="w-full rounded-md border border-gray-200 bg-black"
                  src={block.url}
                />
              ) : null}
              <p className="text-xs text-gray-600 mt-2 break-all">
                {renderInline(block.label)}
              </p>
            </div>
          )
        }

        return <hr key={index} className="my-4 border-gray-200" />
      })}
    </article>
  )
}
